import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCore } from 'contexts/core';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { useServiceSettings } from 'contexts/serviceSettings';
import { attachCoreSubscribers } from 'ducks/core';
import { attachVpcSubscribers } from 'ducks/vpc';
import {
  attachProductSubscribers,
  getMarketProducts,
  hasProducts,
  getUnavailableProducts,
  getProductsNotInMarket,
  getCoreSpecificProducts,
  getAllValidProducts,
  attachDefaultsFromPlannerConfig
} from 'ducks/products';
import { fetchProducts } from 'products/fetchProducts';
import { isMobile } from 'utils/platform';
import { SceneInteractionReporterSystem } from 'systems/SceneInteractionReporterSystem';
import { useAppDispatch } from 'ducks/hooks';
import { getQuery, getRange, stripURLOfQueryParameters } from 'utils/queryUtils';
import { getLocale } from 'i18n/i18nUtils';
import { showOpenDesignSheet, showSaveDesignToProfileSheet } from 'ducks/sheets';
import {
  RemoveProppingInterceptor,
  ReplaceProductInterceptor,
  UpdatedIdInterceptor,
  initSystemsWithMarketProducts
} from '@inter-ikea-storageone/range';
import {
  ReplaceDummyColliderInterceptor,
  ReplaceSideUnitInterceptor
} from '@inter-ikea-storageone/range-ivar';
import { OldBasketAssemblyInterceptor } from '@inter-ikea-storageone/range-pax';
import { setUserName } from 'ducks/userInfo';
import { ToastManager } from '@inter-ikea-kompis/component-toast';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useIntl } from 'react-intl';
import { IAccountServiceUser } from '@inter-ikea-kompis/services';
import { useSaveDesign } from 'hooks/useSaveDesign';
import { useServices } from 'contexts/services';
import { reportUserLoggedIn } from 'analytics/reporters';

type Props = {
  children: React.ReactNode;
};

const InitializePlanner = ({ children }: Props) => {
  const [error, setError] = useState(null);
  const {
    kompisSettings,
    dexfSettings,
    dexfSettings: { general: enableSaveToProfile },
    translations
  } = useServiceSettings();
  const { accountService } = useServices();
  const coreApi = useCore();
  const anyProducts = useSelector(hasProducts);
  const marketProducts = useSelector(getMarketProducts);
  const allValidProducts = useSelector(getAllValidProducts);
  const unavailableProducts = useSelector(getUnavailableProducts);
  const productsNotInMarket = useSelector(getProductsNotInMarket);
  const coreSpecificProducts = useSelector(getCoreSpecificProducts);
  const { plannerConfig, initFeatures } = usePlannerConfig();
  const [resolversRegistered, setResolversRegistered] = useState(false);
  const [coreInitialized, setCoreInitialized] = useState(false);
  const { formatMessage } = useIntl();

  const { saveToProfile } = useSaveDesign();
  const dispatch = useAppDispatch();
  const range = getRange();
  const locale = getLocale();

  const checkLoginStatus = async (): Promise<IAccountServiceUser | null> => {
    const user = await accountService()
      .getUser({ useSilentAuthentication: true })
      .catch(() => null);

    if (user) {
      //Removing state and code from query parameters when logging into IKEA account to clean up the url
      const strippedURL = stripURLOfQueryParameters(['state', 'code']);
      history.replaceState({}, '', strippedURL);
      dispatch(setUserName(user.displayName as string));
    }

    reportUserLoggedIn({ loggedIn: !!user });

    return user;
  };

  useEffect(() => {
    // Top level throw if initializeCore fails
    if (error) throw error;
  }, [error]);

  useEffect(() => {
    async function initializeCore() {
      const config = await fetchProducts(
        kompisSettings,
        dexfSettings,
        translations,
        plannerConfig,
        dispatch
      );
      await coreApi.bootstrap(config);
      plannerConfig.featureToggles.map(toggle => toggle(coreApi));
      initFeatures();
      attachCoreSubscribers(dispatch, coreApi);
      attachVpcSubscribers(dispatch, coreApi);
      attachProductSubscribers(dispatch, coreApi);
      attachDefaultsFromPlannerConfig(dispatch, plannerConfig);
      coreApi.registerSystem(new SceneInteractionReporterSystem(coreApi));
      setCoreInitialized(true);
    }

    initializeCore().catch(setError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coreApi,
    dispatch,
    initFeatures,
    plannerConfig,
    kompisSettings,
    dexfSettings,
    translations,
    range,
    locale
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    let state: string | null = '';
    if (queryParams.has('state')) {
      state = getQuery().get('state');
    }

    if (queryParams.has('error')) {
      new ToastManager({ theme: SkapaTheme }).showToast({
        label: formatMessage({ id: 'storageone.loginErrorOccured' })
      });

      const strippedURL = stripURLOfQueryParameters(['error']);
      history.replaceState({}, '', strippedURL);
    }
    const actOnRedirect = async () => {
      // Make sure core is initialized before checking login status
      if (enableSaveToProfile && coreInitialized) {
        const user = await checkLoginStatus();

        if (state === 'openDesign') {
          dispatch(showOpenDesignSheet(true));
        }
        if (state === 'saveDesign') {
          if (user) {
            saveToProfile();
          } else {
            dispatch(showSaveDesignToProfileSheet());
          }
        }
      }
    };

    actOnRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableSaveToProfile, coreInitialized]);

  useEffect(() => {
    if (coreInitialized) {
      initSystemsWithMarketProducts(marketProducts, coreApi);
      coreApi
        .getMainProductCatalog()
        ?.addInterceptor(
          new ReplaceProductInterceptor(marketProducts, [
            ...unavailableProducts,
            ...productsNotInMarket
          ])
        );
      coreApi.catalogs['platsa']?.addInterceptor(new UpdatedIdInterceptor());

      coreApi.catalogs['pax']?.addInterceptor(new OldBasketAssemblyInterceptor(marketProducts));

      coreApi.catalogs['ivar']?.addInterceptor(
        new ReplaceSideUnitInterceptor(marketProducts, [
          ...unavailableProducts,
          ...productsNotInMarket
        ])
      );
      coreApi.catalogs['ivar']?.addInterceptor(
        new ReplaceDummyColliderInterceptor(coreApi.getMainProductCatalog())
      );
      const proppingCatalog = coreApi.catalogs['propping'];
      proppingCatalog &&
        proppingCatalog.addInterceptor(new RemoveProppingInterceptor(proppingCatalog));
    }
  }, [coreApi, marketProducts, coreInitialized, unavailableProducts, productsNotInMarket]);

  useEffect(() => {
    coreApi.registerResolvers(
      plannerConfig,
      allValidProducts,
      unavailableProducts,
      productsNotInMarket,
      coreSpecificProducts,
      isMobile()
    );

    /**
     * For some reason, the check for products will render children before
     * running this effect and registering resolvers. We need to make sure
     * they are registered with products before rendering children
     */
    setResolversRegistered(!!allValidProducts.length);
  }, [
    coreApi,
    coreSpecificProducts,
    plannerConfig,
    plannerConfig.productGroups,
    allValidProducts,
    productsNotInMarket,
    unavailableProducts
  ]);

  if (!anyProducts || !resolversRegistered || !coreInitialized) {
    return null;
  }

  return children;
};

export default InitializePlanner;
